/**
 * Utility functions for table cell formatting
 */
import { getStatusColor } from '../../../constants/jobStatuses';
import { getInvoiceStatusColor } from '../../../constants/invoiceStatuses';

/**
 * Formats a date string from DD-MM-YYYY format to a localized British format
 * @param dateStr - Date string in DD-MM-YYYY format
 * @returns Formatted date string (e.g., "15 Jan 2024") or original string if invalid
 * @example
 * formatDate("15-01-2024") // Returns "15 Jan 2024"
 * formatDate("invalid") // Returns "invalid"
 * formatDate("32-13-2024") // Returns "32-13-2024"
 */
export function formatDate(dateStr: string) {
  // Validate input string
  if (!dateStr || typeof dateStr !== 'string') {
    return dateStr;
  }

  const parts = dateStr.split('-');

  // Check for expected format (DD-MM-YYYY)
  if (parts.length !== 3) {
    return dateStr;
  }

  const [day, month, year] = parts;

  // Validate parts are numbers
  const dayNum = Number(day);
  const monthNum = Number(month);
  const yearNum = Number(year);

  if (Number.isNaN(dayNum) || Number.isNaN(monthNum) || Number.isNaN(yearNum)) {
    return dateStr;
  }

  // Create date object and validate it's valid
  const date = new Date(yearNum, monthNum - 1, dayNum);

  // Check if the date is valid and matches the input (handles invalid dates like 32-13-2024)
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== yearNum ||
    date.getMonth() !== monthNum - 1 ||
    date.getDate() !== dayNum
  ) {
    return dateStr;
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Splits a frequency string into frequency and day components
 * @param value - Frequency string in format "frequency - day" or just "frequency"
 * @returns Object containing frequency and day properties
 * @example
 * frequencyParts("Weekly - Monday") // Returns { frequency: "Weekly", day: "Monday" }
 * frequencyParts("Monthly") // Returns { frequency: "Monthly", day: undefined }
 */
export function frequencyParts(value: string) {
  const parts = value?.split(' - ') || [];
  const frequency = parts[0] || value;
  const day = parts[1];
  return { frequency, day };
}

/**
 * Formats a number or string as British Pound Sterling currency
 * @param amount - Number or string representing the monetary amount
 * @returns Formatted currency string with GBP symbol and 2 decimal places, or original value if invalid
 * @example
 * moneyFormat(1234.5) // Returns "£1,234.50"
 * moneyFormat("99.99") // Returns "£99.99"
 * moneyFormat("abc") // Returns "abc"
 * moneyFormat(NaN) // Returns "£0.00"
 */
export function moneyFormat(amount: number | string) {
  const num = typeof amount === 'string' ? Number.parseFloat(amount) : amount;

  // Handle NaN values
  if (Number.isNaN(num)) {
    // If original was a string, return it unchanged; if number, format as £0.00
    return typeof amount === 'string' ? amount : '£0.00';
  }

  return num.toLocaleString('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Maps a status value to its corresponding CSS class for styling
 * @param value - Status string (case insensitive)
 * @returns CSS class name for the status (green, red, green-outline)
 * @example
 * getStatusClass("Complete") // Returns "green"
 * getStatusClass("Rejected") // Returns "red"
 * getStatusClass("Unallocated") // Returns "green-outline"
 */
export function getStatusClass(value: string, listingType: string | undefined = undefined): string {
  // Invoices sit outside job statuses, so have their own mapping
  if (listingType === 'invoices') {
    return getInvoiceStatusColor(value);
  }

  return getStatusColor(value);
}

export function capitalizedValue(value: string): string {
  if (value.startsWith(value.charAt(0).toUpperCase())) {
    return value;
  }
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

export function addPaidDate(value: string, paidDate: string | undefined): string {
  if (value === 'paid' && paidDate) {
    const date = new Date(paidDate);
    const formattedDate = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${value} ${formattedDate}`;
  }
  return value;
}
